import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Alert } from 'reactstrap';

import Spinner from '../../components/spinner';
import HttpError from '../../components/http-error';
import withAPI from '../../hocs/with-api';
import '../../date-picker.css';
import 'react-datepicker/dist/react-datepicker.css';
import Table from '../../components/table';
import ActionCard from '../../components/action-card';
import DropdownFilter from '../../components/dropdown-filter';
import ButtonWithIcon from '../../components/button-with-icon';

const REPORT_TYPES = {
  MOST_VIOLATIONS: 'most_violations',
  HIGHEST_SPEEDS: 'highest_speeds',
};

class ReportSpeedViolations extends PureComponent {
  constructor(props) {
    super(props);

    const endDate = moment().toDate();
    const startDate = moment().subtract(6, 'days').toDate();

    this.state = {
      reportData: [],
      startDate,
      endDate,
      reportType: REPORT_TYPES.MOST_VIOLATIONS,
      isLoading: false,
      hasError: false,
      hasData: false,
      isExporting: false,
      dateRangeError: null,
    };
  }

  componentDidMount() {
    const { t } = this.props;
    document.title = `${t('reports.speedViolationsTitle')} - ${t('app.name')}`;
    this.loadReportData();
  }

  getReportTypeOptions = () => {
    const { t } = this.props;

    return [
      {
        id: REPORT_TYPES.MOST_VIOLATIONS,
        label: t('reports.violationsCount'),
        value: REPORT_TYPES.MOST_VIOLATIONS,
      },
      {
        id: REPORT_TYPES.HIGHEST_SPEEDS,
        label: t('reports.highestSpeedViolations'),
        value: REPORT_TYPES.HIGHEST_SPEEDS,
      },
    ];
  };

  getDateRangeErrorMessage = () => {
    const { t } = this.props;
    return t('reports.maxOneWeekRangeNote');
  };

  isDateRangeValid = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return true;
    }

    return moment(endDate).diff(moment(startDate), 'days') <= 6;
  };

  getRequestParams = (extraParams = {}) => {
    const { startDate, endDate, reportType } = this.state;

    return {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
      type: reportType,
      ...extraParams,
    };
  };

  loadReportData = async () => {
    const { httpRequest } = this.props;
    const { startDate, endDate } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    if (!this.isDateRangeValid(startDate, endDate)) {
      this.setState({
        dateRangeError: this.getDateRangeErrorMessage(),
        hasData: false,
        reportData: [],
      });
      return;
    }

    this.setState({
      isLoading: true,
      hasError: false,
      dateRangeError: null,
    });

    try {
      const response = await httpRequest({
        method: 'get',
        url: 'reports/speed-violations',
        data: {
          params: this.getRequestParams(),
        },
      });

      if (response) {
        this.setState({
          reportData: response.data?.data || [],
          isLoading: false,
          hasError: false,
          hasData: true,
        });
      }
    } catch (error) {
      console.error('Speed violations report error:', error);
      this.setState({
        reportData: [],
        isLoading: false,
        hasError: true,
        hasData: false,
      });
    }
  };

  onExportClick = async () => {
    const { httpRequest } = this.props;
    const { startDate, endDate, reportType } = this.state;

    if (!startDate || !endDate) {
      return;
    }

    this.setState({ isExporting: true });

    try {
      const response = await httpRequest({
        method: 'get',
        url: 'reports/speed-violations',
        data: {
          params: this.getRequestParams({ export: 1 }),
        },
        responseType: 'blob',
      });

      if (response) {
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'text/csv',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
        const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
        link.download = `${reportType}-${formattedStartDate}-to-${formattedEndDate}.csv`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Speed violations export error:', error);
    } finally {
      this.setState({ isExporting: false });
    }
  };

  handleSetStartDate = (date) => {
    if (!date) {
      this.setState({
        startDate: null,
        endDate: null,
        reportData: [],
        hasData: false,
      });
      return;
    }

    const endDate = moment(date).add(6, 'days').toDate();

    this.setState(
      {
        startDate: date,
        endDate,
        dateRangeError: null,
      },
      this.loadReportData,
    );
  };

  onReportTypeSelected = (reportType) => {
    this.setState(
      {
        reportType,
        reportData: [],
        hasData: false,
      },
      this.loadReportData,
    );
  };

  renderMostViolationsTable = () => {
    const { t } = this.props;
    const { reportData } = this.state;

    return (
      <Table
        hover
        responsive="xl"
        size="sm"
        isEmpty={_.isEmpty(reportData)}
      >
        <thead>
          <tr>
            <th>{t('drivers.name')}</th>
            <th>{t('carriers.one')}</th>
            <th>{t('drivers.driven_vehicles')}</th>
            <th>{t('vehicles.type')}</th>
            <th>{t('reports.violationsCount')}</th>
          </tr>
        </thead>
        <tbody>
          {_.map(reportData, (item, index) => (
            <tr key={`speed-most-violations-${index}`}>
              <td>{item.driver_name || '-'}</td>
              <td>{item.carrier_name || '-'}</td>
              <td>{item.vehicles_driven || '-'}</td>
              <td>{item.vehicle_types || '-'}</td>
              <td>{item.violations_count || 0}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderHighestSpeedsTable = () => {
    const { t } = this.props;
    const { reportData } = this.state;

    return (
      <Table
        hover
        responsive="xl"
        size="sm"
        isEmpty={_.isEmpty(reportData)}
      >
        <thead>
          <tr>
            <th>{t('drivers.name')}</th>
            <th>{t('carriers.one')}</th>
            <th>{t('vehicle')}</th>
            <th>{t('vehicles.type')}</th>
            <th>{t('reports.registeredSpeed')}</th>
            <th>{t('reports.contraventionValue')}</th>
            <th>{t('reports.location')}</th>
          </tr>
        </thead>
        <tbody>
          {_.map(reportData, (item, index) => (
            <tr key={`speed-highest-${index}`}>
              <td>{item.driver_name || '-'}</td>
              <td>{item.carrier_name || '-'}</td>
              <td>{item.vehicle_number || '-'}</td>
              <td>{item.vehicle_type || '-'}</td>
              <td>{item.max_speed ? `${item.max_speed} km/h` : '-'}</td>
              <td>{item.contravention_value || '-'}</td>
              <td>{item.location || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderReportTable = () => {
    const { reportType } = this.state;

    if (reportType === REPORT_TYPES.HIGHEST_SPEEDS) {
      return this.renderHighestSpeedsTable();
    }

    return this.renderMostViolationsTable();
  };

  render() {
    const { t } = this.props;
    const {
      startDate,
      endDate,
      reportType,
      isLoading,
      hasError,
      hasData,
      isExporting,
      dateRangeError,
      reportData,
    } = this.state;

    return (
      <ActionCard
        title={t('reports.speedViolations')}
        withActionButton={false}
        headerChildren={[
          <div className="inline-block mr-2 mb-2">
            <DropdownFilter
              dropDownStyles={{ toggleStyles: { maxWidth: '200px' } }}
              options={this.getReportTypeOptions()}
              label={t('reports.selectReportType')}
              onSelected={this.onReportTypeSelected}
              selectedValue={reportType}
              withReset={false}
            />
          </div>,
          <div className="inline-block mr-2 mb-2">
            <DatePicker
              onChange={this.handleSetStartDate}
              selected={startDate}
              className="input-clean center-text kpi-date-picker"
              dateFormat="dd/MM/yyyy"
              maxDate={moment().toDate()}
              placeholderText={t('reports.selectStartDate')}
            />
          </div>,
          <div className="inline-block mr-2 mb-2">
            <DatePicker
              selected={endDate}
              className="input-clean center-text kpi-date-picker"
              dateFormat="dd/MM/yyyy"
              disabled
              placeholderText={t('reports.selectEndDate')}
            />
          </div>,
          <ButtonWithIcon
            disabled={_.isEmpty(reportData) || isExporting}
            onClick={this.onExportClick}
            icon="/assets/save-icon.png"
            title={t('kpi.export')}
            loading={isExporting}
          />,
        ]}
      >
        <>
          {dateRangeError && (
            <Alert color="warning" className="mb-3">
              {dateRangeError}
            </Alert>
          )}

          {(!startDate || !endDate) && !dateRangeError && (
            <div>
              <p>{t('reports.selectStartDateToViewSpeedViolations')}</p>
            </div>
          )}

          {startDate && endDate && !dateRangeError && (
            <>
              {isLoading && <Spinner />}

              {hasError && (
                <HttpError
                  withTryAgainButton
                  onTryAgain={this.loadReportData}
                />
              )}

              {!isLoading && !hasError && hasData && this.renderReportTable()}
            </>
          )}
        </>
      </ActionCard>
    );
  }
}

ReportSpeedViolations.propTypes = {
  t: PropTypes.func.isRequired,
  httpRequest: PropTypes.func.isRequired,
};

export default compose(
  withTranslation(),
  withAPI,
  withRouter,
)(ReportSpeedViolations);
